/**
 * Admin authentication and capability context.
 *
 * The frontend deliberately holds NO role -> permission matrix. It asks the
 * backend which permissions the signed-in operator actually holds, via
 * /api/admin/capabilities, and renders from that. The role model has exactly one
 * owner (shared/constants/operator_permissions.py) and the UI cannot drift out
 * of agreement with it.
 *
 * Fail-safe by construction: `permissions` is null until a capability fetch has
 * succeeded, and can() returns false for null. A slow or failed fetch therefore
 * renders the UI as if the operator can do nothing, never briefly as if they
 * were an Owner. Hiding a control is only ever a convenience — the backend
 * enforces the same permission on every route, so direct URL or API access is
 * refused regardless of what the UI drew.
 *
 * SESSION END IS ONE EVENT, SEEN FROM EVERY REQUEST (7 Sep 2026)
 * ----------------------------------------------------------------
 * An operator token is an 8-hour absolute JWT with no refresh. Before this,
 * the only 401 the UI understood was the one on the capabilities fetch at
 * mount; every page's own axios / fetch call got the same 401 and rendered an
 * error, a blank table, or nothing, with the dead token still in localStorage.
 * Now:
 *   - a token whose `exp` has passed is never presented: the provider drops
 *     it at mount and schedules the drop for a token that is still alive;
 *   - EVERY 401 from the Admin API — axios (interceptor) and raw fetch (a
 *     wrapper installed once by this provider) — ends the session;
 *   - ending the session remembers the current path, so
 *     `AdminProtectedRoute` routes to /login and the login page returns the
 *     operator to where they were.
 * The pure pieces live in ./sessionExpiry.js and are tested there.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  isAdminApiRequest,
  isTokenExpired,
  msUntilExpiry,
  rememberReturnPath,
  setSessionNotice,
} from './sessionExpiry';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;

const AdminAuthContext = createContext(null);

const currentPath = () => {
  try {
    const { pathname, search, hash } = window.location;
    return `${pathname}${search || ''}${hash || ''}`;
  } catch (_) {
    return null;
  }
};

export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  // null = not yet known. Never default to a permissive value.
  const [permissions, setPermissions] = useState(null);
  const [platformConfig, setPlatformConfig] = useState(null);
  const [capabilityError, setCapabilityError] = useState(false);
  const [loading, setLoading] = useState(true);
  // Bumped on every session end so a page can react (re-render to /login).
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const expiryTimer = useRef(null);

  const clearSession = useCallback(() => {
    localStorage.removeItem('admin_token');
    setAdminUser(null);
    setPermissions(null);
    setPlatformConfig(null);
  }, []);

  /**
   * The ONE way a session ends without the operator asking. Remembers where
   * they were, records why, clears the token, and re-renders the guard.
   */
  const expireSession = useCallback((reason = 'expired') => {
    rememberReturnPath(currentPath());
    setSessionNotice(reason);
    clearSession();
    setSessionEpoch((n) => n + 1);
  }, [clearSession]);

  const loadCapabilities = useCallback(async (token) => {
    try {
      const { data } = await axios.get(`${ADMIN_API}/capabilities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdminUser(data.operator);
      setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
      setPlatformConfig(data.config || null);
      setCapabilityError(false);
      return true;
    } catch (error) {
      // A 401 means the session is gone. Anything else (network, 5xx) leaves
      // permissions null, so the UI stays locked down rather than guessing.
      if (error?.response?.status === 401) {
        expireSession('expired');
      } else {
        setPermissions(null);
        setCapabilityError(true);
      }
      return false;
    }
  }, [expireSession]);

  // ── mount: never present a token that has already expired ────────────────
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { setLoading(false); return; }
    if (isTokenExpired(token)) {
      expireSession('expired');
      setLoading(false);
      return;
    }
    loadCapabilities(token).finally(() => setLoading(false));
  }, [loadCapabilities, expireSession]);

  // ── the clock: drop the token the moment its `exp` passes ────────────────
  useEffect(() => {
    if (expiryTimer.current) { clearTimeout(expiryTimer.current); expiryTimer.current = null; }
    if (!adminUser) return undefined;
    const token = localStorage.getItem('admin_token');
    const ms = msUntilExpiry(token);
    if (ms === null) return undefined;
    expiryTimer.current = setTimeout(() => expireSession('expired'), ms);
    return () => { if (expiryTimer.current) clearTimeout(expiryTimer.current); };
  }, [adminUser, expireSession]);

  // ── every 401 from the Admin API ends the session ─────────────────────────
  useEffect(() => {
    const onAxiosError = (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url;
      if (status === 401 && isAdminApiRequest(url)) expireSession('expired');
      return Promise.reject(error);
    };
    const id = axios.interceptors.response.use((r) => r, onAxiosError);

    // Pages call `fetch` directly (audit logs, usage, integrations …). One
    // wrapper, installed while the provider is mounted, restored when it is
    // not, so those calls are covered without touching every page.
    const originalFetch = window.fetch;
    const wrapped = async (input, init) => {
      const response = await originalFetch(input, init);
      try {
        const url = typeof input === 'string' ? input : input?.url;
        if (response && response.status === 401 && isAdminApiRequest(url)) expireSession('expired');
      } catch (_) { /* never let the guard break a request */ }
      return response;
    };
    wrapped.__adminSessionGuard = true;
    window.fetch = wrapped;

    return () => {
      axios.interceptors.response.eject(id);
      if (window.fetch === wrapped) window.fetch = originalFetch;
    };
  }, [expireSession]);

  const adminLogin = async (email, password) => {
    try {
      const { data } = await axios.post(`${ADMIN_API}/login`, { email, password });
      localStorage.setItem('admin_token', data.access_token);
      setSessionNotice(null);
      // Re-fetch rather than trusting the login payload, so a refresh and a
      // fresh login converge on exactly the same capability state.
      await loadCapabilities(data.access_token);
      return { success: true, user: data.user, mfaRequired: !!data.mfa_required };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? (detail.map((d) => d.msg).filter(Boolean).join('; ') || 'Admin login failed')
          : 'Admin login failed';
      return { success: false, error: message };
    }
  };

  const adminLogout = async () => {
    const token = localStorage.getItem('admin_token');
    // Server-side revocation bumps token_version, invalidating other sessions
    // too. Best-effort: a failure here must still clear the local session.
    if (token) {
      try {
        await axios.post(`${ADMIN_API}/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (_) { /* fall through to local clear */ }
    }
    // Deliberate sign-out: no return path, no notice.
    setSessionNotice(null);
    clearSession();
  };

  const getAdminToken = () => localStorage.getItem('admin_token');

  /** True only when the backend has confirmed this permission. */
  const can = useCallback(
    (permission) => Array.isArray(permissions) && permissions.includes(permission),
    [permissions],
  );

  /** True if the operator holds at least one of the given permissions. */
  const canAny = useCallback(
    (...perms) => perms.flat().some((p) => can(p)),
    [can],
  );

  return (
    <AdminAuthContext.Provider value={{
      adminUser,
      permissions,
      platformConfig,
      capabilityError,
      capabilitiesLoaded: Array.isArray(permissions),
      loading,
      sessionEpoch,
      adminLogin,
      adminLogout,
      expireSession,
      getAdminToken,
      can,
      canAny,
      isAuthenticated: !!adminUser,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return context;
};

export default AdminAuthContext;
